import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { identityApi } from '../services/api';

// Shared Action Required state — landing-and-action-required PRD.
//
// Sean's PRD-ASK answer Q4 (2026-05-04): the top-bar bell icon and the
// Action Required tile MUST share a single API hit per page-load
// ("one shared API hit, not two"). This context fetches once on mount
// and exposes:
//   { items, count, loading, error, refresh, sourceState }
//
// `sourceState` lets consumers (the empty-state copy switch in
// <ActionRequired>) distinguish:
//   - 'loading'         — fetch in flight
//   - 'error'           — fetch failed
//   - 'no_properties'   — owner has zero linked properties
//   - 'onboarding_one'  — single-property owner whose property is <30d old
//   - 'no_items'        — properties exist + items list empty
//   - 'has_items'       — items present
//
// Per Sean's Q3, empty-state copy is differentiated, so the consumer
// reads sourceState rather than `items.length`.
//
// Provider should be mounted at the App layout level (one instance per
// page-load). Both <ActionRequired> and <BellIcon> consume this hook.

const ActionRequiredContext = createContext(null);

export function ActionRequiredProvider({ children }) {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fire both in parallel — properties needed for empty-state classifier.
      // Properties result is cheap; on-page-load only.
      const [arRes, propsRes] = await Promise.all([
        identityApi.getActionRequired(),
        identityApi.getProperties()
      ]);

      if (arRes.status === 200 && Array.isArray(arRes.data?.items)) {
        // Sort ascending — lowest urgency_score = most urgent (BRD §7C).
        const sorted = [...arRes.data.items].sort(
          (a, b) => (a.urgency_score ?? 99999) - (b.urgency_score ?? 99999)
        );
        setItems(sorted);
      } else {
        setItems([]);
        setError({ scope: 'action_required', status: arRes.status });
      }

      if (propsRes.status === 200 && Array.isArray(propsRes.data?.properties)) {
        setProperties(propsRes.data.properties);
      } else {
        // Soft-fail: leave properties empty but don't block the tile.
        setProperties([]);
      }
    } catch (err) {
      console.error('[useActionRequired] fetch failed:', err && err.message);
      setError({ scope: 'action_required', message: err && err.message });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Derive the sourceState used by empty-state copy switching.
  // Heuristic for "single-property onboarding": exactly one property AND
  // its created_at is within the last 30 days. Approximate — flag in code.
  const sourceState = (() => {
    if (loading) return 'loading';
    if (error) return 'error';
    if (items.length > 0) return 'has_items';
    if (properties.length === 0) return 'no_properties';
    if (properties.length === 1) {
      // APPROXIMATE — Properties payload may or may not include created_at.
      // Falls back to 'no_items' if absent.
      const created = properties[0]?.created_at || properties[0]?.CREATEDTIME;
      if (created) {
        const ageDays = (Date.now() - new Date(created).getTime()) / (1000 * 60 * 60 * 24);
        if (Number.isFinite(ageDays) && ageDays >= 0 && ageDays <= 30) {
          return 'onboarding_one';
        }
      }
    }
    return 'no_items';
  })();

  const value = {
    items,
    count: items.length,
    loading,
    error,
    refresh: fetchAll,
    sourceState
  };

  return (
    <ActionRequiredContext.Provider value={value}>
      {children}
    </ActionRequiredContext.Provider>
  );
}

export function useActionRequired() {
  const ctx = useContext(ActionRequiredContext);
  if (!ctx) {
    // Safe fallback so a consumer rendered outside the provider doesn't
    // crash. Returns inert state — bell renders 0, tile shows error copy.
    return {
      items: [],
      count: 0,
      loading: false,
      error: { scope: 'no_provider' },
      refresh: () => {},
      sourceState: 'error'
    };
  }
  return ctx;
}
